import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "@/api/auth";
import { ApiError } from "@/api/client";
import { Btn } from "@/components/Btn";
import { Card } from "@/components/Card";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

export function LoginPage() {
  useDocumentTitle("Anmelden");
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await login(username, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Benutzername oder Passwort falsch.");
      } else {
        setError(err instanceof Error ? err.message : "Anmeldung fehlgeschlagen.");
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "var(--font-body)",
      }}
    >
      <Card style={{ width: "min(420px, 100%)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: "var(--brand-100)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img src="/app/LogoPZRM.png" alt="" style={{ width: 36, height: 36, objectFit: "contain" }} />
          </div>
          <div>
            <div className="display" style={{ fontSize: 20, color: "var(--brand-900)" }}>
              Raumünzach
            </div>
            <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>Buchungssystem anmelden</div>
          </div>
        </div>

        <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--fg-muted)" }}>
            Benutzername
            <input
              type="text"
              autoFocus
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              style={inputStyle}
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--fg-muted)" }}>
            Passwort
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{ ...inputStyle, paddingRight: 40, width: "100%", boxSizing: "border-box" }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                aria-pressed={showPassword}
                title={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                style={eyeButtonStyle}
              >
                <span className="material-icons-outlined" style={{ fontSize: 18 }}>
                  {showPassword ? "visibility_off" : "visibility"}
                </span>
              </button>
            </div>
          </label>
          {error && (
            <div style={{ fontSize: 12, color: "var(--status-cancel)", fontWeight: 600 }}>{error}</div>
          )}
          <Btn type="submit" variant="primary" disabled={pending} style={{ justifyContent: "center" }}>
            {pending ? "Anmelden…" : "Anmelden"}
          </Btn>
        </form>
      </Card>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "8px 10px",
  border: "1px solid var(--border-strong)",
  borderRadius: 8,
  background: "var(--bg-card)",
  color: "var(--fg)",
  fontFamily: "var(--font-body)",
  fontSize: 14,
};

const eyeButtonStyle: React.CSSProperties = {
  position: "absolute",
  right: 6,
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: 4,
  display: "flex",
  alignItems: "center",
  lineHeight: 1,
  color: "var(--fg-muted)",
};
